import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { X, Calendar, AlertCircle, FolderLock, FileText, Users as UsersIcon } from "lucide-react";
import { toast } from "@/components/Toaster";

// Same two-letter initials rule used on the Kanban cards — first name +
// surname, or the first two letters of the email local-part as a fallback.
const initials = (u) => {
  if (!u) return "??";
  const name = (u.name || "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  const email = (u.email || "").trim();
  if (email) return email.split("@")[0].slice(0, 2).toUpperCase();
  return "??";
};

// Deterministic avatar tone — kept in lock-step with the same palette
// used by Tasks.jsx so the same user lands on the same colour everywhere.
const AVATAR_TONES = [
  "bg-emerald-500/15 text-emerald-500",
  "bg-sky-500/15 text-sky-500",
  "bg-amber-500/20 text-amber-600 dark:text-amber-400",
  "bg-rose-500/15 text-rose-500",
  "bg-violet-500/15 text-violet-500",
  "bg-teal-500/15 text-teal-500",
  "bg-fuchsia-500/15 text-fuchsia-500",
  "bg-indigo-500/15 text-indigo-500",
];
const avatarTone = (u) => {
  const key = String((u?.id || u?.email || u?.name || "") + "");
  if (!key) return AVATAR_TONES[0];
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return AVATAR_TONES[h % AVATAR_TONES.length];
};

/**
 * TaskFormModal — create or edit a task.
 *
 * Linkable VDR objects: data room → folder → document, cascading so the
 * picker only shows folders/documents that exist inside the chosen room.
 * Multi-assignee with a simple chip list; small enough for the boardroom
 * use-case (typically < 20 users), full multiselect can come later.
 */
const STATUSES = [
  { key: "todo",         label: "To do" },
  { key: "in_progress",  label: "In progress" },
  { key: "blocked",      label: "Blocked" },
  { key: "done",         label: "Done" },
];
const PRIORITIES = [
  { key: "low",    label: "Low" },
  { key: "medium", label: "Medium" },
  { key: "high",   label: "High" },
  { key: "urgent", label: "Urgent" },
];

export default function TaskFormModal({ mode, task, users, rooms, onClose, onSaved }) {
  const editing = mode === "edit" && !!task;
  const [title, setTitle] = useState(task?.title || "");
  const [description, setDescription] = useState(task?.description || "");
  const [status, setStatus] = useState(task?.status || "todo");
  const [priority, setPriority] = useState(task?.priority || "medium");
  const [dueDate, setDueDate] = useState(task?.due_date || "");
  const [assignees, setAssignees] = useState(task?.assignees || []);
  const [roomId, setRoomId] = useState(task?.room_id || "");
  const [folderId, setFolderId] = useState(task?.folder_id || "");
  const [documentId, setDocumentId] = useState(task?.document_id || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [docs, setDocs] = useState([]);

  // Cascading picker — pull documents for the selected room so the user
  // can link to a folder or file inside it.
  useEffect(() => {
    if (!roomId) { setDocs([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/rooms/${roomId}/documents`);
        setDocs(data);
      } catch (e) {
        setDocs([]);
      }
    })();
  }, [roomId]);

  const folders = useMemo(() => docs.filter((d) => d.type === "folder"), [docs]);
  const files = useMemo(() => {
    if (!folderId) return docs.filter((d) => d.type === "file");
    return docs.filter((d) => d.type === "file" && d.parent_id === folderId);
  }, [docs, folderId]);

  const toggleAssignee = (id) => {
    setAssignees((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setErr("Title is required"); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        status,
        priority,
        due_date: dueDate || null,
        assignees,
        room_id: roomId || null,
        folder_id: folderId || null,
        document_id: documentId || null,
      };
      let resp;
      if (editing) {
        resp = await api.patch(`/tasks/${task.id}`, payload);
      } else {
        resp = await api.post("/tasks", payload);
      }
      toast.success(editing ? "Task updated" : "Task created");
      onSaved?.(resp.data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 relative"
        data-testid="task-form-modal"
      >
        <button
          type="button" onClick={onClose} aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition"
        >
          <X className="w-4 h-4" />
        </button>
        <h3 className="font-display text-2xl tracking-tight">
          {editing ? "Edit task" : "New task"}
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Link this task to a data room, folder, or document so reviewers land on the right context.
        </p>

        <div className="mt-5 space-y-5">
          <div>
            <label className="label-mono">Title</label>
            <input
              required value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="task-title-input"
              className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="e.g. Review Q4 financials with auditor"
            />
          </div>

          <div>
            <label className="label-mono">Description</label>
            <textarea
              rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
              data-testid="task-description-input"
              className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-y"
              placeholder="Add context, expected outcome, or links…"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="label-mono">Status</label>
              <select
                value={status} onChange={(e) => setStatus(e.target.value)}
                data-testid="task-status-select"
                className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono">Priority</label>
              <select
                value={priority} onChange={(e) => setPriority(e.target.value)}
                data-testid="task-priority-select"
                className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Due date</label>
              <input
                type="date"
                value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                data-testid="task-due-input"
                className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* Linked objects */}
          <div className="border border-border rounded-md p-4 space-y-3">
            <div className="label-mono text-muted-foreground inline-flex items-center gap-1.5">
              <FolderLock className="w-3 h-3" /> Linked workspace objects
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="label-mono">Data Room</label>
                <select
                  value={roomId}
                  onChange={(e) => { setRoomId(e.target.value); setFolderId(""); setDocumentId(""); }}
                  data-testid="task-room-select"
                  className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">— None —</option>
                  {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-mono">Folder</label>
                <select
                  value={folderId}
                  onChange={(e) => { setFolderId(e.target.value); setDocumentId(""); }}
                  disabled={!roomId}
                  data-testid="task-folder-select"
                  className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                >
                  <option value="">— None —</option>
                  {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-mono">Document</label>
                <select
                  value={documentId}
                  onChange={(e) => setDocumentId(e.target.value)}
                  disabled={!roomId}
                  data-testid="task-document-select"
                  className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                >
                  <option value="">— None —</option>
                  {files.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Assignees */}
          <div>
            <label className="label-mono inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" /> Assignees</label>
            {users.length === 0 ? (
              <div className="mt-2 text-xs text-muted-foreground">No users available.</div>
            ) : (
              <div className="mt-2 max-h-44 overflow-y-auto border border-border rounded-md divide-y divide-border">
                {users.map((u) => (
                  <label
                    key={u.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer hover:bg-secondary/50"
                    data-testid={`task-assignee-${u.id}`}
                  >
                    <span className="flex items-center gap-2 text-sm">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-semibold tracking-wide ${avatarTone(u)}`}>
                        {initials(u)}
                      </span>
                      <span className="text-foreground">{u.name || u.email}</span>
                      <span className="label-mono text-muted-foreground">{u.email}</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={assignees.includes(u.id)}
                      onChange={() => toggleAssignee(u.id)}
                      className="accent-primary"
                    />
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {err && (
          <div className="mt-4 text-sm text-destructive flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" /> {err}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition">
            Cancel
          </button>
          <button
            type="submit" disabled={busy}
            data-testid="task-submit"
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
          >
            {busy ? "Saving…" : editing ? "Save changes" : "Create task"}
          </button>
        </div>
      </form>
    </div>
  );
}
